"use client";

import { fontWeights } from "@/utils/fonts";
import { Lock, Zap, RefreshCw } from "lucide-react";
import React from "react";

const SERVER_URL = process.env.NEXT_PUBLIC_SERVER_URL;

const ICON_MAP = {
  lock: Lock,
  zap: Zap,
  refresh: RefreshCw
};

export default function StaffAugmentationCTA({
  title,
  description,
  ctaText,
  ctaLink,
  image,
  badges = []
}: {
  title?: string;
  description?: string;
  ctaText?: string;
  ctaLink?: string;
  image?: string;
  badges?: { text: string, icon: string }[];
}) {
  const imageUrl = image?.startsWith('http') ? image : `${SERVER_URL}/${image}`;

  return (
    <section
      className="relative overflow-hidden h-auto tsm-350:h-[430px] sm:h-[400px] md:h-[450px] lg:h-[480px] xl:h-[610px] 2xl:h-[650px]"
      style={{ background: "radial-gradient(ellipse at 50% 44%, #004498 0%, #002F6D 82%)" }}
    >
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1905 876"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 0L16.8089 4.58825C321.74 87.8239 636.413 130 952.5 130C1268.59 130 1583.26 87.8239 1888.19 4.58826L1905 0V0H0V0Z" fill="white" />
        <mask id="mask0_58_2" style={{ maskType: "alpha" }} maskUnits="userSpaceOnUse" x="0" y="0" width="1905" height="2000">
          <path d="M0 0L16.8089 4.58825C321.74 87.8239 636.413 130 952.5 130C1268.59 130 1583.26 87.8239 1888.19 4.58826L1905 0V2000H0V0Z" fill="white" />
        </mask>
        <g mask="url(#mask0_58_2)">
          <g clipPath="url(#clip0_58_2)">
            <g opacity="0.41">
              <g filter="url(#filter0_f_58_2)">
                <rect x="212" y="-258" width="1482" height="1482" rx="741" stroke="white" strokeOpacity="0.48" strokeWidth="80" />
              </g>
              <g filter="url(#filter1_f_58_2)">
                <rect x="375" y="-95" width="1156" height="1156" rx="578" stroke="white" strokeOpacity="0.88" strokeWidth="20" />
              </g>
              <g filter="url(#filter2_f_58_2)">
                <rect x="622" y="152" width="662" height="662" rx="331" stroke="white" strokeOpacity="0.88" strokeWidth="20" />
              </g>
            </g>
          </g>
        </g>
        <defs>
          <filter id="filter0_f_58_2" x="67.1" y="-402.9" width="1771.8" height="1771.8" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="52.45" result="effect1_foregroundBlur_58_2" />
          </filter>
          <filter id="filter1_f_58_2" x="255.5" y="-214.5" width="1395" height="1395" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="54.75" result="effect1_foregroundBlur_58_2" />
          </filter>
          <filter id="filter2_f_58_2" x="502.5" y="32.5" width="901" height="901" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="54.75" result="effect1_foregroundBlur_58_2" />
          </filter>
          <clipPath id="clip0_58_2">
            <rect width="1904" height="981" fill="white" transform="translate(1 -105)" />
          </clipPath>
        </defs>
      </svg>

      <div className="relative z-10 mx-auto w-full max-w-[1920px] 
              px-4 
              sm:px-8 
              lg:px-0 
              max-w-auto
              sm:max-w-[760px]
              md:max-w-[810px] 
              lg:max-w-[960px] 
              xl:max-w-[1200px] 
              2xl:max-w-[1303px]
              mt-20 
              tsm-350:mt-20
              tsm-400:mt-20
              sm:mt-[90px]
              md:mt-[100px] 
              lg:mt-[120px] 
              xl:mt-[160px] 
              2xl:mt-[170px] 
              pb-[10px]
              tsm-350:pb-0
              ">
        <div className="mx-auto w-full">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-1.5 tsm-350:gap-4 sm:gap-[26px] md:gap-12 lg:gap-16 xl:gap-[99px] justify-center items-center">
            {/* Left Content */}
            <div className="w-full max-w-[352px] md:max-w-[440px] lg:max-w-[600px] xl:max-w-[743px] text-center sm:text-left">
              {title && (
                <h2
                  className="text-white tracking-normal sm:tracking-tight xl:tracking-[-2px] 
                  

                    text-[22px]
                    sm:text-[32px]
                    md:text-[35px]
                    xl:text-[52px]
                    2xl:text-[60px]
                    leading-[22px]
                    tsm-350:leading-[24px] 
                    tsm-400:leading-[24px] 
                    sm:leading-[35px] 
                    md:leading-[40px] 
                    xl:leading-[50px]
                    2xl:leading-[67px]
                    mb-2.5
                    sm:mb-[18px]
                    lg:mb-[22px]
                    xl:mb-[30px]
                    w-full
                    "
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.regular }}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              )}

              {/* Badges */}
              <div className="flex flex-wrap justify-center sm:justify-start gap-1.5 lg:gap-2 xl:gap-[9px] mb-2 sm:mb-2 md:mb-4 lg:mb-6 xl:mb-8">
                {badges.map((badge, idx) => {
                  const Icon = ICON_MAP[badge.icon as keyof typeof ICON_MAP] || Lock;
                  return (
                    <div
                      key={idx}
                      className="flex items-center bg-white rounded-[4.7px] gap-1.5 lg:gap-[7px] xl:gap-2 shadow-md transition-transform hover:-translate-y-1 duration-300
                        px-2 
                        sm:px-2 
                        md:px-2 
                        lg:px-2.5 
                        xl:px-3 

                        py-1
                        sm:py-1.5 
                        md:py-1.5 
                        lg:py-[9px] 
                        xl:py-2.5 
                      ">
                      <Icon className="w-3 md:w-3 lg:w-3.5 xl:w-4      h-3 sm:h-3 lg:h-3.5 xl:h-4 text-[#002F6D]" />
                      <span
                        className="text-[#002F6D] text-[10px] sm:text-[12px] lg:text-[14px] xl:text-[16px]"
                        style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
                      >
                        {badge.text}
                      </span>
                    </div>
                  );
                })}
              </div>

              {description && (
                <p
                  className="text-[#FFFFFF] mx-auto lg:mx-0  
                    mb-[5px]
                    tsm-350:mb-[10px]
                    sm:mb-[15px]
                    lg:mb-[20px]
                    xl:mb-[31px]   
                    text-[11px] 
                    sm:text-[14px] 
                    lg:text-[16px] 
                    xl:text-[19px] 
                    leading-[14px]
                    smleading-[18px] 
                    md:leading-[20px] 
                    lg:leading-[24px]
                    xl:leading-[32px]"
                  style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                >
                  {description}
                </p>
              )}

              {/* CTA Button */}
              {ctaText && (
                <div className="mx-auto lg:mx-0">
                  <button
                    className="bg-white rounded-[4.7px] md:rounded-[6px] text-[#0F172A] shadow-lg transition-all hover:bg-[#E8F0FE] duration-300 whitespace-nowrap
                      
                      px-2
                      sm:px-3
                      md:px-3.5 
                      lg:px-3.5 
                      xl:px-[15px] 

                      py-1
                      sm:py-1.5
                      md:py-1.5
                      lg:py-2.5 
                      xl:py-2.5 
                      text-[10px] 
                      tsm-350:text-[11px] 
                      tsm-400:text-[12px] 
                      sm:text-[13px] 
                      md:text-[14px] 
                      lg:text-[15px] 
                      xl:text-[16px]"
                    style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
                    onClick={() => window.open(ctaLink, "_blank")}
                  >
                    {ctaText}
                  </button>
                </div>
              )}
            </div>

            {/* Right Illustration */}
            <div className=" flex justify-center lg:justify-end">
              <div className="relative   w-[100px] tsm-350:w-[120px] tsm-400:w-[130px] sm:w-[200px] md:w-full h-auto">
                {image && (
                  <img
                    src={imageUrl}
                    alt="Hire Top IT Talent Illustration"
                    className="w-full h-auto object-contain drop-shadow-2xl"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
